import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { AdminTitle } from "@/admin/AdminLayout";
import { RecordedSave } from "@/admin/Recorded";
import { ImageField } from "@/admin/ImageField";
import { inputCls } from "@/components/common";
import { Switch } from "@/components/ui/switch";

const KEYS = ["enabled", "eyebrow", "heading", "text", "gift_enabled", "gift_text", "gift_label", "gift_url", "button", "delay", "scroll", "days", "image"];
const editable = (d: any) => Object.fromEntries(KEYS.map((k) => [k, ["delay", "scroll", "days"].includes(k) ? Number(d[k]) : d[k]]));

const T = ({ d, set, k, l, area = false }: any) => (
  <label className="block text-[11px] text-neutral-500">{l}
    {area ? <textarea rows={3} value={d[k] || ""} onChange={(e) => set({ ...d, [k]: e.target.value })} className={`${inputCls} text-sm mt-1`} data-testid={`popup-${k}`} />
      : <input value={d[k] ?? ""} onChange={(e) => set({ ...d, [k]: e.target.value })} className={`${inputCls} text-sm mt-1`} data-testid={`popup-${k}`} />}
  </label>
);

export default function AdminPopup() {
  const [d, setD] = useState<any>(null);
  const load = () => api.get("/admin-x/newsletter-popup").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const save = async () => { try { const r = await api.put("/admin-x/newsletter-popup", editable(d)); setD(r.data); toast("Sign-up popup saved"); return true; } catch (e) { toast.error(errMsg(e)); return false; } };
  return (
    <div data-testid="admin-popup">
      <AdminTitle title="Sign-up Popup" sub="Invites visitors to join your mailing list for giveaway reminders. Never shown to people who've signed up, to signed-in admins, or at checkout.">
        {d && <RecordedSave res="settings" id="newsletter_popup" onSave={save} value={editable(d)} testId="popup-save" onRestored={load} />}
      </AdminTitle>
      {d && <div className="grid lg:grid-cols-2 gap-4 max-w-5xl">
        <div className="border border-neutral-200 bg-white p-5 space-y-3">
          <label className="flex items-center gap-3 text-sm"><Switch checked={d.enabled} onCheckedChange={(v: boolean) => setD({ ...d, enabled: v })} data-testid="popup-enabled" />Show the sign-up popup</label>
          <p className="text-xs text-neutral-500" data-testid="popup-subscriber-count">{d.popup_subscribers} subscriber{d.popup_subscribers === 1 ? "" : "s"} so far from the popup</p>
          <T d={d} set={setD} k="eyebrow" l="Small heading" />
          <T d={d} set={setD} k="heading" l="Heading" />
          <T d={d} set={setD} k="text" l="Text" area />
          <T d={d} set={setD} k="button" l="Button" />
          <div><p className="text-[11px] text-neutral-500 mb-1">Image (left side, desktop)</p><ImageField value={d.image} onChange={(v: string) => setD({ ...d, image: v })} testId="popup-image" /></div>
        </div>
        <div className="space-y-4">
          <div className="border border-neutral-200 bg-white p-5 space-y-3">
            <p className="eyebrow text-[10px] text-neutral-500">When it appears</p>
            <div className="grid grid-cols-3 gap-3">
              <label className="text-[11px] text-neutral-500">After (seconds)<input type="number" min={0} max={300} value={d.delay} onChange={(e) => setD({ ...d, delay: e.target.value })} className={`${inputCls} text-sm mt-1`} data-testid="popup-delay" /></label>
              <label className="text-[11px] text-neutral-500">Or scrolled (% of page)<input type="number" min={0} max={100} value={d.scroll} onChange={(e) => setD({ ...d, scroll: e.target.value })} className={`${inputCls} text-sm mt-1`} data-testid="popup-scroll" /></label>
              <label className="text-[11px] text-neutral-500">Show again after (1–30 days)<input type="number" min={1} max={30} value={d.days} onChange={(e) => setD({ ...d, days: e.target.value })} className={`${inputCls} text-sm mt-1`} data-testid="popup-days" /></label>
            </div>
            <p className="text-xs text-neutral-500">It shows at most once per visit. After someone closes it, it stays away for the days you set; once they sign up, it never shows again.</p>
          </div>
          <div className="border border-neutral-200 bg-white p-5 space-y-3">
            <label className="flex items-center gap-3 text-sm"><Switch checked={d.gift_enabled} onCheckedChange={(v: boolean) => setD({ ...d, gift_enabled: v })} data-testid="popup-gift-enabled" />Sign-up gift</label>
            <T d={d} set={setD} k="gift_text" l="Gift line in the popup and welcome email" />
            <div className="grid grid-cols-2 gap-3"><T d={d} set={setD} k="gift_label" l="Gift button" /><T d={d} set={setD} k="gift_url" l="Gift link" /></div>
          </div>
        </div>
      </div>}
    </div>
  );
}
